/**
 * Live SOL/USD price, fetched through the Slurp backend (which proxies
 * Jupiter + CoinGecko server-side to dodge browser CORS). Returns null on
 * hard failure — callers must block launches until a real number is available.
 */

import { useEffect, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";
const REFRESH_MS = 30_000;

export async function fetchSolPrice() {
  try {
    const res = await fetch(`${API_BASE}/api/sol-price`);
    if (!res.ok) throw new Error(`sol-price ${res.status}`);
    const json = await res.json();
    const price = Number(json?.price);
    if (!Number.isFinite(price) || price <= 0) throw new Error("no price");
    return price;
  } catch (err) {
    console.warn("SOL price fetch failed:", err);
    return null;
  }
}

/**
 * React hook — exposes the live SOL/USD price. `null` while loading or after
 * a hard failure. Refreshes every 30s while the component is mounted.
 */
export function useSolPrice() {
  const [price, setPrice] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    const tick = async () => {
      const p = await fetchSolPrice();
      if (cancelled.current) return;
      if (p != null) {
        setPrice(p);
        setUpdatedAt(Date.now());
      }
    };
    tick();
    const id = setInterval(tick, REFRESH_MS);
    return () => {
      cancelled.current = true;
      clearInterval(id);
    };
  }, []);

  return { price, updatedAt };
}
